import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import "../../Stylesheets/tableV2.css";
import { cx } from "../../Util/classHelpers";
import Pagination from "../CommonComponents/Pagination";

const TableV2Header = ({ columns }) => {
  const visibleColumns = useMemo(() => columns.filter(col => col?.show ?? true), [columns]);
  
  return (
    <thead>
      <tr>
        {visibleColumns.map(column => (
          <th key={column.id} scope="col" className={column?.headerClass ?? ""}>{column.label}</th>
        ))}
      </tr>
    </thead>
  );
};

const TableV2Body = ({ data, columns, emptyText }) => {
  const visibleColumns = useMemo(() => columns.filter(col => col?.show ?? true), [columns]);

  if (data.length === 0) {
    return (
      <tbody>
        <tr>
          <td colSpan={visibleColumns.length} className="text-center" data-empty-text>
            {emptyText}
          </td>
        </tr>
      </tbody>
    );
  }

  return (
    <tbody>
      {data.map((row, rowIndex) => (
        <tr key={rowIndex}>
          {visibleColumns.map(column => (
            <td key={column.id} className={column?.tableClass ?? ""}>
              {column.data(row)}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
};

// Utility to find the nearest scrollable ancestor
function getScrollParent(node) {
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    if (/(auto|scroll)/.test(style.overflowX)) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

const TableV2 = ({ data = [], config, emptyText = "No data available" }) => {
  const { columns, pagination, pageSize, scrollable, hoverable = true } = config;
  const [currentPage, setCurrentPage] = useState(1);
  const tableRef = useRef();

  useEffect(() => {
    setCurrentPage(1);
  }, [data]);

  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;

    const container = getScrollParent(table);
    if (!container) return;

    const handleScrollAndResize = () => {
      const isSticking = container.scrollLeft + container.clientWidth >= container.scrollWidth - 1;
      table.setAttribute("data-right-sticking", isSticking ? "true" : "false");
    };

    container.addEventListener("scroll", handleScrollAndResize);
    setTimeout(() => handleScrollAndResize(), 500);

    // Add ResizeObserver for container
    let resizeObserver = null;
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        handleScrollAndResize();
      });
      resizeObserver.observe(container);
    }

    return () => {
      container.removeEventListener("scroll", handleScrollAndResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  const paginatedData = useMemo(() => 
    pagination 
      ? data.slice((currentPage - 1) * pageSize, currentPage * pageSize)
      : data,
    [pagination, data, currentPage, pageSize]
  );

  const totalPages = useMemo(() => 
    Math.ceil(data.length / pageSize),
    [data.length, pageSize]
  );

  return (
    <div className="table-v2-wrapper">
      <div className={cx(scrollable && "table-responsive")}> 
        <table
          ref={tableRef}
          className={cx("table", hoverable && "table-hover", pagination && "table-pagination")}
        >
          <TableV2Header columns={columns} />
          <TableV2Body data={paginatedData} columns={columns} emptyText={emptyText} />
        </table>
      </div>
      {pagination && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalEntries={data.length}
            onPageChange={setCurrentPage}
          />
        )}
    </div>
  );
};

export default TableV2;
